import { useState, useEffect, useRef, useCallback } from 'react';

export type OrientationAngle = 0 | 90 | 180 | 270;

/**
 * Detects physical device orientation via accelerometer / Screen Orientation API.
 * Returns the device angle: 0 (portrait), 90 (landscape-right), 270 (landscape-left), 180 (upside-down).
 *
 * On iOS 13+ the DeviceOrientation API requires a user gesture to grant permission.
 * Call `requestPermission()` from a tap handler (e.g. capture "ready" screen).
 * Fallback methods (screen.orientation, window.orientation) work without permission.
 */
export const useDeviceOrientation = (): {
  angle: OrientationAngle;
  /** Call from a user-gesture handler to request iOS accelerometer permission */
  requestPermission: () => Promise<boolean>;
} => {
  const [angle, setAngle] = useState<OrientationAngle>(0);
  const lastAngleRef = useRef<OrientationAngle>(0);
  const listenerAdded = useRef(false);

  const normalizeAngle = useCallback((deg: number): OrientationAngle => {
    const normalized = ((Math.round(deg / 90) * 90) % 360 + 360) % 360;
    return normalized as OrientationAngle;
  }, []);

  // DeviceOrientation handler
  const handleDeviceOrientation = useCallback((event: DeviceOrientationEvent) => {
    const { beta, gamma } = event;
    if (beta === null || gamma === null) return;

    const absGamma = Math.abs(gamma);
    const absBeta = Math.abs(beta);

    let newAngle: OrientationAngle = 0;
    if (absGamma > 45 && absGamma < 135) {
      newAngle = gamma > 0 ? 90 : 270;
    } else if (absBeta > 135) {
      newAngle = 180;
    }

    if (newAngle !== lastAngleRef.current) {
      lastAngleRef.current = newAngle;
      setAngle(newAngle);
    }
  }, []);

  const addDeviceListener = useCallback(() => {
    if (listenerAdded.current) return;
    listenerAdded.current = true;
    window.addEventListener('deviceorientation', handleDeviceOrientation);
  }, [handleDeviceOrientation]);

  // Exposed function — must be called from a user gesture on iOS 13+
  const requestPermission = useCallback(async (): Promise<boolean> => {
    if (!window.DeviceOrientationEvent) return false;
    const DOE = DeviceOrientationEvent as unknown as {
      requestPermission?: () => Promise<string>;
    };
    if (typeof DOE.requestPermission !== 'function') {
      // Non-iOS — permission not required, listener already added
      addDeviceListener();
      return true;
    }
    try {
      const result = await DOE.requestPermission();
      if (result === 'granted') {
        addDeviceListener();
        return true;
      }
    } catch { /* denied or not in gesture context */ }
    return false;
  }, [addDeviceListener]);

  useEffect(() => {
    // Screen Orientation API handler
    const handleScreenOrientation = () => {
      const newAngle = normalizeAngle(screen.orientation?.angle ?? 0);
      if (newAngle !== lastAngleRef.current) {
        lastAngleRef.current = newAngle;
        setAngle(newAngle);
      }
    };

    // Legacy window.orientation handler
    const handleWindowOrientation = () => {
      const raw = (window as unknown as { orientation?: number }).orientation ?? 0;
      const newAngle = normalizeAngle(raw);
      if (newAngle !== lastAngleRef.current) {
        lastAngleRef.current = newAngle;
        setAngle(newAngle);
      }
    };

    // On non-iOS, add DeviceOrientation listener immediately (no permission needed)
    if (window.DeviceOrientationEvent) {
      const DOE = DeviceOrientationEvent as unknown as {
        requestPermission?: () => Promise<string>;
      };
      if (typeof DOE.requestPermission !== 'function') {
        addDeviceListener();
      }
    }

    // Register fallbacks
    if (screen.orientation) {
      handleScreenOrientation();
      screen.orientation.addEventListener('change', handleScreenOrientation);
    }

    const hasWindowOrientation =
      typeof (window as unknown as { orientation?: number }).orientation !== 'undefined';
    if (hasWindowOrientation) {
      window.addEventListener('orientationchange', handleWindowOrientation);
    }

    return () => {
      if (listenerAdded.current) {
        window.removeEventListener('deviceorientation', handleDeviceOrientation);
      }
      screen.orientation?.removeEventListener('change', handleScreenOrientation);
      if (hasWindowOrientation) {
        window.removeEventListener('orientationchange', handleWindowOrientation);
      }
    };
  }, [normalizeAngle, addDeviceListener, handleDeviceOrientation]);

  return { angle, requestPermission };
};
